// ---------------------------------------------------------------------------
// Doc DSL Renderer — multi-page Konva document renderer.
//
// Renders a written document layout (headings, paragraphs, lists, tables,
// images) across multiple pages using Konva canvas, reusing existing VizDsl
// block renderers for charts, maps, tile rows, bullet lists, etc.
//
// Each rendered Konva node carries _semantic metadata for DOCX export.
// ---------------------------------------------------------------------------

import Konva from "konva";
import CustomText from "./CustomText";
import CustomImage from "./CustomImage";
import { themeColor, defaultTheme } from "./renderers/plainObjectRenderer";

// Reuse existing VizDsl block renderers for visual blocks
import {
  renderChart as vizRenderChart,
  renderTileRow as vizRenderTileRow,
  renderBulletList as vizRenderBulletList,
  renderMap as vizRenderMap,
  renderTextBlock as vizRenderTextBlock,
  renderConditional as vizRenderConditional,
  renderLegend as vizRenderLegend,
} from "./renderers";
import { isContentSized, measureNaturalHeight } from "./VizDslRenderer";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CONTENT_SIZED_DOC_TYPES = new Set([
  "heading", "paragraph", "list", "table", "image", "section"
]);

// VizDsl block types that are reused inside documents
const VIZ_BLOCK_TYPES = new Set([
  "chart", "tile_row", "bullet_list", "map", "text_block", "legend"
]);

// Heading level → font size mapping
const HEADING_FONT_SIZES = { 1: 24, 2: 20, 3: 17, 4: 14, 5: 12, 6: 11 };
// Vertical rhythm (px). Space ABOVE a heading gives a visual break between sections;
// these values are recorded into _semantic so the DOCX export mirrors the same rhythm.
const HEADING_SPACE_BEFORE = { 1: 20, 2: 16, 3: 14, 4: 12, 5: 10, 6: 10 };
const HEADING_SPACE_AFTER  = { 1: 10, 2: 8, 3: 6, 4: 6, 5: 4, 6: 4 };
const TEXT_FILL = "#1a1a1a";          // body + heading text colour
const PARAGRAPH_SPACE_AFTER = 8;
const LIST_ITEM_SPACE_AFTER = 4;
const LIST_INDENT = 20;               // px per nesting level

// Default page dimensions (US Letter at 96 DPI)
const DEFAULT_PAGE_WIDTH = 816;
const DEFAULT_PAGE_HEIGHT = 1056;
const DEFAULT_MARGINS = { top: 40, bottom: 40, left: 32, right: 32 };
const DEFAULT_CHROME_HEIGHT = { header: 24, footer: 20 };

// ---------------------------------------------------------------------------
// DocPaginationEngine — page-aware flow engine
//
// Renders blocks across multiple pages, splitting content when needed.
// Produces an array of Konva Groups (one per page).
// ---------------------------------------------------------------------------

export class DocPaginationEngine {
  /**
   * @param {Object} pageConfig - page dimensions and margins
   * @param {Object} chrome - header/footer config
   * @param {Object} globalOpts - shared rendering options (theme, fontScale, etc.)
   */
  constructor(pageConfig, chrome, globalOpts) {
    this.pageConfig = pageConfig;
    this.chrome = chrome;
    this.globalOpts = globalOpts;

    const { width = DEFAULT_PAGE_WIDTH, height = DEFAULT_PAGE_HEIGHT } = pageConfig;
    const margins = pageConfig.margins || DEFAULT_MARGINS;
    const chromeH = chrome
      ? (chrome.header?.height || DEFAULT_CHROME_HEIGHT.header) +
        (chrome.footer?.height || DEFAULT_CHROME_HEIGHT.footer)
      : 0;

    this.pageWidth = width;
    this.pageHeight = height;
    this.margins = margins;
    this.bodyWidth = width - margins.left - margins.right;
    this.bodyHeight = height - margins.top - margins.bottom - chromeH;

    this.pages = [];
    this.currentPage = null;
  }

  /**
   * Build the document by flowing blocks across pages.
   * @param {Array} flow - DSL flow array
   * @param {Object} crystalizedData - crystallized data
   * @returns {Konva.Group[]} array of page groups
   */
  buildDocument(flow, crystalizedData) {
    this.startNewPage(1);

    for (const block of flow) {
      this.addBlock(block, crystalizedData);
    }

    return this.pages;
  }

  /**
   * Add a block to the current page, splitting or page-breaking as needed.
   */
  addBlock(block, crystalizedData) {
    const remaining = this.currentPage.remainingHeight;
    const bodyWidth = this.bodyWidth;

    // Section wrapper — handle page_break_before/after and recurse into children
    if (block.type === "section") {
      if (block.page_break_before) {
        this.startNewPage(this.currentPage.pageNumber + 1);
      }
      if (block.heading) {
        // Render heading as part of the section
        const headingBlock = {
          type: "heading",
          level: block.heading.level || 1,
          text: block.heading.text || "",
          id: block.heading.id
        };
        this.addBlock(headingBlock, crystalizedData);
      }
      const children = block.children || [];
      for (const child of children) {
        this.addBlock(child, crystalizedData);
      }
      if (block.page_break_after) {
        this.startNewPage(this.currentPage.pageNumber + 1);
      }
      return;
    }

    // Explicit page break
    if (block.type === "page_break") {
      this.startNewPage(this.currentPage.pageNumber + 1);
      return;
    }

    // Measure the block's natural height
    const natH = this.measureBlockNaturalHeight(block, crystalizedData);

    // Block fits on current page. Render at the block's NATURAL height, not the full
    // remaining space — otherwise viz blocks (charts/tile rows) expand to fill the page,
    // pushing following blocks past the body into the footer.
    if (natH !== null && natH <= remaining) {
      const group = this.renderBlock(block, crystalizedData, bodyWidth, natH);
      if (group) {
        this.placeOnPage(group, natH);
      }
      return;
    }

    // Block doesn't fit — try to split
    if (this.canSplit(block)) {
      const split = this.splitBlock(block, crystalizedData, remaining, bodyWidth);
      if (split.first) {
        const firstGroup = this.renderBlock(split.first, crystalizedData, bodyWidth, remaining);
        if (firstGroup) {
          this.placeOnPage(firstGroup, null);
        }
      }
      this.startNewPage(this.currentPage.pageNumber + 1);
      if (split.second) {
        const secondGroup = this.renderBlock(split.second, crystalizedData, bodyWidth, this.bodyHeight);
        if (secondGroup) {
          this.placeOnPage(secondGroup, null);
        }
      }
      return;
    }

    // Can't split — push to next page
    this.startNewPage(this.currentPage.pageNumber + 1);
    const group = this.renderBlock(block, crystalizedData, bodyWidth, this.bodyHeight);
    if (group) {
      this.placeOnPage(group, natH || 100);
    }
  }

  /**
   * Place a rendered block group at the current Y cursor and advance the cursor.
   */
  placeOnPage(group, fallbackHeight) {
    const page = this.currentPage;
    group.y(page.cursorY);
    page.group.add(group);
    const h = group.height() || fallbackHeight || 0;
    page.cursorY += h;
    page.remainingHeight -= h;
  }

  /**
   * Measure the natural height of a block at the document's body width.
   */
  measureBlockNaturalHeight(block, crystalizedData) {
    const w = this.bodyWidth;
    const HUGE = 99999;

    if (block.type === "paragraph") {
      // Measure via CustomText
      const probe = new CustomText({
        width: w,
        text: block.text || "",
        fontSize: block.fontSize || 12,
        lineHeight: block.lineHeight || 1.4
      });
      const h = probe.height();
      probe.destroy();
      return h + (block.spacingAfter || 8);
    }

    if (block.type === "heading") {
      const level = block.level || 1;
      const fontSize = HEADING_FONT_SIZES[level] || 14;
      const probe = new CustomText({
        width: w,
        text: block.text || "",
        fontSize,
        fontStyle: "bold"
      });
      const h = probe.height();
      probe.destroy();
      // Mirror renderHeading: spaceBefore + text + (rule ? 8 : 0) + spaceAfter
      const spaceBefore = HEADING_SPACE_BEFORE[level] ?? 10;
      const spaceAfter = HEADING_SPACE_AFTER[level] ?? 6;
      return spaceBefore + h + (level <= 2 ? 8 : 0) + spaceAfter;
    }

    if (block.type === "list") {
      const items = block.items || [];
      let totalH = 0;
      for (const item of items) {
        const text = typeof item === "string" ? item : (item.text || "");
        const probe = new CustomText({
          width: w - 20, // indent
          text,
          fontSize: 12,
          lineHeight: 1.4
        });
        totalH += probe.height() + 6;
        probe.destroy();
      }
      return totalH + 8;
    }

    if (block.type === "table") {
      // Use the resolved rows (crystallized, normalised + capped) so the measured
      // height matches what renderTable actually draws — otherwise the table overflows.
      const rows = this.resolveBlockRows(block, crystalizedData);
      return Math.max(rows.length, 1) * 28; // ~28px per row
    }

    if (block.type === "image") {
      const imgW = block.width || w;
      return Math.min(imgW * 0.75, 400) + 30; // image + caption
    }

    // VizDsl blocks — use existing measurement
    if (VIZ_BLOCK_TYPES.has(block.type)) {
      const h = measureNaturalHeight(block, crystalizedData, w, this.globalOpts.fontScale || 1,
        this.globalOpts.theme, this.globalOpts.debug, this.globalOpts.fontScale || 1);
      return h !== null ? h : 200;
    }

    return null;
  }

  /**
   * Can this block type be split across pages?
   */
  canSplit(block) {
    return block.type === "paragraph" ||
      block.type === "list" ||
      block.type === "table";
  }

  /**
   * Split a block into first-part (fits remaining) and second-part (next page).
   */
  splitBlock(block, crystalizedData, remainingHeight, bodyWidth) {
    if (block.type === "paragraph") {
      const probe = new CustomText({
        width: bodyWidth,
        text: block.text || "",
        fontSize: block.fontSize || 12,
        lineHeight: block.lineHeight || 1.4
      });
      const textArr = probe.textArr || [];
      probe.destroy();

      // Find how many lines fit in remainingHeight
      const lineH = probe.height() / Math.max(textArr.length, 1);
      const linesFit = Math.max(1, Math.floor(remainingHeight / (lineH || 20)));
      const charsPerLine = Math.max(1, Math.floor((block.text || "").length / Math.max(textArr.length, 1)));

      const splitIdx = Math.min(charsPerLine * linesFit, (block.text || "").length);
      const firstText = (block.text || "").slice(0, splitIdx).replace(/\s+\S*$/, "");
      const secondText = (block.text || "").slice(splitIdx).trim();

      return {
        first: { ...block, text: firstText, _isContinuation: false },
        second: secondText ? { ...block, text: secondText, _isContinuation: true } : null
      };
    }

    if (block.type === "list") {
      const items = block.items || [];
      let h = 0;
      let splitIdx = 0;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const text = typeof item === "string" ? item : (item.text || "");
        const probe = new CustomText({
          width: bodyWidth - 20,
          text,
          fontSize: 12,
          lineHeight: 1.4
        });
        const itemH = probe.height() + 6;
        probe.destroy();
        if (h + itemH > remainingHeight) break;
        h += itemH;
        splitIdx = i + 1;
      }
      if (splitIdx === 0) splitIdx = 1; // at least one item

      return {
        first: { ...block, items: items.slice(0, splitIdx) },
        second: { ...block, items: items.slice(splitIdx) }
      };
    }

    if (block.type === "table") {
      const rows = block.rows || [];
      const rowH = 28;
      const rowsFit = Math.max(1, Math.floor(remainingHeight / rowH));

      return {
        first: { ...block, rows: rows.slice(0, rowsFit) },
        second: { ...block, rows: rows.slice(rowsFit) }
      };
    }

    return { first: block, second: null };
  }

  /**
   * Resolve text for a doc block: prefer crystallized data, fall back to inline block text.
   */
  resolveBlockText(block, crystalizedData) {
    const blockId = block.id;
    const crystalData = blockId ? crystalizedData[blockId] : null;
    if (crystalData && crystalData.text) return crystalData.text;
    if (block.text) return block.text;
    return "";
  }

  /**
   * Resolve runs for a paragraph: prefer crystallized data, fall back to inline block runs.
   */
  resolveBlockRuns(block, crystalizedData) {
    const blockId = block.id;
    const crystalData = blockId ? crystalizedData[blockId] : null;
    if (crystalData && crystalData.runs && crystalData.runs.length > 0) return crystalData.runs;
    if (block.runs && block.runs.length > 0) return block.runs;
    return null;
  }

  /**
   * Resolve list items for a list block: prefer crystallized data, fall back to inline items.
   */
  resolveBlockItems(block, crystalizedData) {
    const blockId = block.id;
    const crystalData = blockId ? crystalizedData[blockId] : null;
    if (crystalData && crystalData.items && crystalData.items.length > 0) return crystalData.items;
    if (block.items && block.items.length > 0) return block.items;
    return [];
  }

  /**
   * Resolve table rows for a table block: prefer crystallized data, fall back to inline rows.
   */
  resolveBlockRows(block, crystalizedData) {
    const blockId = block.id;
    const crystalData = blockId ? crystalizedData[blockId] : null;
    let rawRows = (crystalData && Array.isArray(crystalData.rows) && crystalData.rows.length > 0)
      ? crystalData.rows
      : (Array.isArray(block.rows) && block.rows.length > 0 ? block.rows : []);
    if (rawRows.length === 0) return [];

    // For a pre-defined table, keep the declared row count — strict array bounds are
    // stripped for some providers, so the LLM can over-generate rows.
    if (crystalData?.rows && Array.isArray(block.rows) && block.rows.length > 0 && rawRows.length > block.rows.length) {
      rawRows = rawRows.slice(0, block.rows.length);
    }

    // The crystallizer emits rows as objects ({ col_0, col_1, …, is_header }); the renderer
    // wants arrays of cell strings. Normalise so cells aren't silently empty.
    const colCount = block.cols
      || (Array.isArray(rawRows[0]) ? rawRows[0].length
        : Object.keys(rawRows[0] || {}).filter(k => /^col_\d+$/.test(k)).length)
      || 0;
    return rawRows.map(row => {
      if (Array.isArray(row)) return row;
      if (row && typeof row === "object") {
        const arr = [];
        for (let ci = 0; ci < colCount; ci++) {
          const v = row[`col_${ci}`];
          arr.push(v == null ? "" : v);
        }
        return arr;
      }
      return [String(row == null ? "" : row)];
    });
  }

  /**
   * Render a single block at the given width/height.
   */
  renderBlock(block, crystalizedData, width, height) {
    const opts = {
      x: this.margins.left,
      y: this.margins.top,
      width,
      height,
      imageCallback: this.globalOpts.imageCallback,
      theme: this.globalOpts.theme,
      fontScale: this.globalOpts.fontScale || 1,
      debug: this.globalOpts.debug,
      provenance: this.globalOpts.provenance,
      crystalizedData
    };

    switch (block.type) {
      case "heading":
        return this.renderHeading(block, crystalizedData, opts);
      case "paragraph":
        return this.renderParagraph(block, crystalizedData, opts);
      case "list":
        return this.renderList(block, crystalizedData, opts);
      case "table":
        return this.renderTable(block, crystalizedData, opts);
      case "image":
        return this.renderImage(block, crystalizedData, opts);
      case "conditional":
        return this.renderConditional(block, crystalizedData, opts);
      case "page_break":
        return null;
      default:
        // Delegate to existing VizDsl renderers for chart/map/tile_row/bullet_list/text_block
        return this.renderVizBlock(block, crystalizedData, opts);
    }
  }

  /**
   * Start a new page with chrome (header/footer).
   */
  startNewPage(pageNumber) {
    const { width, height } = this.pageConfig;
    const margins = this.margins;
    const chrome = this.chrome;
    const s = this.globalOpts.fontScale || 1;

    const pageGroup = new Konva.Group({
      x: 0, y: 0, width, height
    });

    // Page background
    pageGroup.add(new Konva.Rect({
      width, height,
      fill: "#ffffff"
    }));

    // Page border (subtle outline)
    pageGroup.add(new Konva.Rect({
      x: 0, y: 0, width, height,
      stroke: "#e0e0e0", strokeWidth: 1
    }));

    // Render chrome header
    if (chrome?.header) {
      const hdrH = chrome.header.height || DEFAULT_CHROME_HEIGHT.header;
      const hdrY = margins.top - hdrH - 4;
      if (chrome.header.left) {
        const text = chrome.header.left
          .replace("{{page}}", String(pageNumber))
          .replace("{{total}}", "?");
        pageGroup.add(new CustomText({
          x: margins.left, y: hdrY,
          text, fontSize: Math.round(9 * s),
          fill: "#888"
        }));
      }
      if (chrome.header.right) {
        const text = chrome.header.right
          .replace("{{page}}", String(pageNumber))
          .replace("{{total}}", "?");
        const rightText = new CustomText({
          x: 0, y: hdrY,
          width: width - margins.right,
          text, fontSize: Math.round(9 * s),
          fill: "#888", align: "right"
        });
        rightText.x(width - margins.right - rightText.width());
        pageGroup.add(rightText);
      }
      // Header bottom line
      pageGroup.add(new Konva.Line({
        points: [margins.left, hdrY + hdrH, width - margins.right, hdrY + hdrH],
        stroke: "#ccc", strokeWidth: 0.5
      }));
    }

    // Render chrome footer
    if (chrome?.footer) {
      const ftrH = chrome.footer.height || DEFAULT_CHROME_HEIGHT.footer;
      const ftrY = height - margins.bottom + 4;
      // Footer top line
      pageGroup.add(new Konva.Line({
        points: [margins.left, ftrY, width - margins.right, ftrY],
        stroke: "#ccc", strokeWidth: 0.5
      }));
      if (chrome.footer.left) {
        const text = chrome.footer.left
          .replace("{{page}}", String(pageNumber))
          .replace("{{total}}", "?")
          .replace("{{date}}", new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }));
        pageGroup.add(new CustomText({
          x: margins.left, y: ftrY + 2,
          text, fontSize: Math.round(8 * s),
          fill: "#888"
        }));
      }
      if (chrome.footer.right) {
        const text = chrome.footer.right
          .replace("{{page}}", String(pageNumber))
          .replace("{{total}}", "?")
          .replace("{{date}}", new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }));
        const rightText = new CustomText({
          x: 0, y: ftrY + 2,
          width: width - margins.right,
          text, fontSize: Math.round(8 * s),
          fill: "#888", align: "right"
        });
        rightText.x(width - margins.right - rightText.width());
        pageGroup.add(rightText);
      }
    }

    const bodyTop = margins.top;
    const chromeH = chrome
      ? (chrome.header?.height || DEFAULT_CHROME_HEIGHT.header) +
        (chrome.footer?.height || DEFAULT_CHROME_HEIGHT.footer)
      : 0;
    const bodyH = height - margins.top - margins.bottom - chromeH;

    this.currentPage = {
      group: pageGroup,
      pageNumber,
      remainingHeight: bodyH,
      bodyTop,
      cursorY: bodyTop
    };

    this.pages.push(pageGroup);
  }

  // ── NEW doc-specific block renderers ──────────────────────────────────

  /**
   * renderHeading — H1-H6 with level-based font sizing and semantic tagging.
   */
  renderHeading(block, crystalizedData, options) {
    const { x, y, width, fontScale = 1, theme } = options;
    const s = fontScale;
    const level = block.level || 1;
    const text = this.resolveBlockText(block, crystalizedData);
    const fontSize = Math.round((HEADING_FONT_SIZES[level] || 14) * s);
    const bold = level <= 4;
    const spaceBefore = Math.round((HEADING_SPACE_BEFORE[level] ?? 10) * s);
    const spaceAfter = Math.round((HEADING_SPACE_AFTER[level] ?? 6) * s);
    const hasRule = level <= 2;

    if (!text) return null;

    const group = new Konva.Group({ x, y, width });

    // Heading text — offset down by spaceBefore to create a visual break above
    const headingNode = new CustomText({
      x: 0, y: spaceBefore, width,
      text,
      fontSize,
      fontStyle: bold ? "bold" : "normal",
      fill: TEXT_FILL,
      lineHeight: 1.3
    });
    group.add(headingNode);

    // Decoration for H1/H2: horizontal rule below
    let contentBottom = spaceBefore + headingNode.height();
    if (hasRule) {
      const ruleY = contentBottom + 4;
      group.add(new Konva.Line({
        points: [0, ruleY, width, ruleY],
        stroke: "#333", strokeWidth: 1
      }));
      contentBottom = ruleY + 4;
    }
    group.height(contentBottom + spaceAfter);

    // Semantic tagging for DOCX export — record the actual style + rhythm so the
    // exported doc matches this preview.
    group.setAttr("_semantic", {
      blockType: "heading",
      headingLevel: level,
      headingId: block.id || block.headingId || null,
      fontSizePx: fontSize,
      color: TEXT_FILL,
      bold,
      rule: hasRule,
      spacingBeforePx: spaceBefore,
      spacingAfterPx: spaceAfter
    });

    return group;
  }

  /**
   * renderParagraph — text paragraph with inline markdown formatting.
   */
  renderParagraph(block, crystalizedData, options) {
    const { x, y, width, fontScale = 1, theme } = options;
    const s = fontScale;
    const text = this.resolveBlockText(block, crystalizedData);
    const runs = this.resolveBlockRuns(block, crystalizedData) || [];
    const fontSize = Math.round((block.fontSize || 12) * s);
    const lineHeight = block.lineHeight || 1.4;

    if (!text && runs.length === 0) return null;

    const group = new Konva.Group({ x, y, width });

    // Build markdown text from runs for CustomText rendering
    let markdownText = "";
    if (runs.length > 0) {
      markdownText = runs.map(r => {
        let t = r.text || "";
        if (r.bold) t = `**${t}**`;
        if (r.italic) t = `_${t}_`;
        return t;
      }).join("");
    } else {
      markdownText = text;
    }

    const paraNode = new CustomText({
      x: 0, y: 0, width,
      text: markdownText,
      fontSize,
      lineHeight,
      withMarkdown: true,
      fill: "#1a1a1a",
      align: block.alignment || "left"
    });
    group.add(paraNode);

    const spacingAfter = block.spacingAfter || PARAGRAPH_SPACE_AFTER;
    const paraH = paraNode.height() + spacingAfter;
    group.height(paraH);

    // Semantic tagging for DOCX export — record style + spacing to mirror this preview.
    group.setAttr("_semantic", {
      blockType: "paragraph",
      runs: runs.length > 0 ? runs : [{ text, bold: false, italic: false }],
      fontSizePx: fontSize,
      color: TEXT_FILL,
      lineHeight,
      alignment: block.alignment || "left",
      spacingBeforePx: 0,
      spacingAfterPx: spacingAfter
    });

    return group;
  }

  /**
   * renderList — ordered or unordered list with nesting.
   */
  renderList(block, crystalizedData, options) {
    const { x, y, width, fontScale = 1 } = options;
    const s = fontScale;
    const style = block.style || "bullet"; // "bullet" | "numbered" | "lettered"
    const items = this.resolveBlockItems(block, crystalizedData);
    const fontSize = Math.round(12 * s);
    const lineHeight = 1.4;
    const indent = Math.round(LIST_INDENT * s);

    if (items.length === 0) return null;

    const group = new Konva.Group({ x, y, width });
    let cy = 0;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const text = typeof item === "string" ? item : (item.text || "");
      const level = item.level || 0;
      const itemIndent = indent * (level + 1);

      // Bullet/number marker
      let marker = "";
      if (style === "bullet") {
        const bullets = ["•", "◦", "▸"];
        marker = bullets[level] || "•";
      } else if (style === "numbered") {
        marker = `${i + 1}.`;
      } else if (style === "lettered") {
        marker = `${String.fromCharCode(97 + (i % 26))}.`;
      }

      const markerNode = new CustomText({
        x: 0, y: cy,
        text: marker,
        fontSize,
        fill: TEXT_FILL,
        width: itemIndent
      });
      group.add(markerNode);

      // Item text
      const itemNode = new CustomText({
        x: itemIndent, y: cy,
        width: width - itemIndent,
        text,
        fontSize,
        fill: TEXT_FILL,
        lineHeight,
        withMarkdown: /\*\*[^*]+\*\*|_[^_]+_/.test(text)
      });
      group.add(itemNode);

      // Semantic tagging per item — record indent + style so the export matches.
      itemNode.setAttr("_semantic", {
        blockType: "listItem",
        listType: style,
        listLevel: level,
        listIndex: i,
        fontSizePx: fontSize,
        color: TEXT_FILL,
        indentPx: itemIndent,
        spacingAfterPx: LIST_ITEM_SPACE_AFTER
      });

      const itemH = Math.max(markerNode.height(), itemNode.height()) + LIST_ITEM_SPACE_AFTER;
      cy += itemH;
    }

    group.height(cy + 4);

    // Tag the container group
    group.setAttr("_semantic", {
      blockType: "list",
      listStyle: style
    });

    return group;
  }

  /**
   * renderTable — table grid with headers, column widths, merged cells.
   */
  renderTable(block, crystalizedData, options) {
    const { x, y, width, fontScale = 1 } = options;
    const s = fontScale;
    const rows = this.resolveBlockRows(block, crystalizedData);
    const cols = block.cols || (rows.length > 0 ? rows[0].length : 0);
    const widths = block.widths || [];
    const hasHeader = block.header_row !== false;

    if (rows.length === 0) return null;

    const group = new Konva.Group({ x, y, width });
    const cellH = Math.round(26 * s);
    const fontSize = Math.round(11 * s);
    const borderColor = "#ccc";
    const headerFill = "#f0f0f0";

    // Compute column x positions from proportional widths
    const colXs = [0];
    const totalWidth = widths.reduce((a, b) => a + b, 0);
    if (totalWidth > 0) {
      for (let ci = 1; ci < cols; ci++) {
        const prevPct = widths[ci - 1] || 0;
        colXs.push(colXs[ci - 1] + (prevPct / totalWidth) * width);
      }
    } else {
      const colW = width / cols;
      for (let ci = 1; ci < cols; ci++) {
        colXs.push(ci * colW);
      }
    }
    const colWidths = [];
    for (let ci = 0; ci < cols; ci++) {
      const start = colXs[ci];
      const end = colXs[ci + 1] || width;
      colWidths.push(end - start);
    }

    // Render each row
    for (let ri = 0; ri < rows.length; ri++) {
      const row = rows[ri];
      const rowY = ri * cellH;
      const isHeader = hasHeader && ri === 0;

      // Row background for header
      if (isHeader) {
        group.add(new Konva.Rect({
          x: 0, y: rowY, width, height: cellH,
          fill: headerFill
        }));
      }

      for (let ci = 0; ci < cols && ci < row.length; ci++) {
        const cellText = String(row[ci] || "");
        const cellX = colXs[ci];
        const cellW = colWidths[ci] - 4;

        const cell = new CustomText({
          x: cellX + 4, y: rowY + 2,
          width: cellW,
          text: cellText,
          fontSize,
          fontStyle: isHeader ? "bold" : "normal",
          fill: "#1a1a1a",
          lineHeight: 1.2
        });
        group.add(cell);

        // Semantic tagging per cell
        const cellFill = (typeof row[ci] === "object" && row[ci]?.fill) || (isHeader ? headerFill : undefined);
        cell.setAttr("_semantic", {
          blockType: "tableCell",
          row: ri,
          col: ci,
          isHeader,
          colspan: 1,
          fontSizePx: fontSize,
          color: "#1a1a1a",
          fill: cellFill
        });
      }
    }

    // Border lines (horizontal)
    for (let ri = 0; ri <= rows.length; ri++) {
      const y = ri * cellH;
      const isHeaderLine = ri === 1 && hasHeader;
      group.add(new Konva.Line({
        points: [0, y, width, y],
        stroke: isHeaderLine ? "#999" : borderColor,
        strokeWidth: isHeaderLine ? 1.5 : 0.5
      }));
    }
    // Border lines (vertical)
    for (let ci = 0; ci <= cols; ci++) {
      const x = ci < colXs.length ? colXs[ci] : width;
      group.add(new Konva.Line({
        points: [x, 0, x, rows.length * cellH],
        stroke: borderColor, strokeWidth: 0.5
      }));
    }

    group.height(rows.length * cellH);

    // Tag the container group — record cell padding + row height so the export matches
    // this preview's tight table layout (cells drawn at x+4, y+2 within cellH rows).
    group.setAttr("_semantic", {
      blockType: "table",
      totalRows: rows.length,
      totalCols: cols,
      cellPadXPx: 4,
      cellPadYPx: 2,
      rowHeightPx: cellH,
      borderColor,
      borderWidthPx: 0.5,
      headerLineColor: "#999",
      headerLineWidthPx: 1.5,
      headerFill
    });

    return group;
  }

  /**
   * renderImage — image with caption.
   */
  renderImage(block, crystalizedData, options) {
    const { x, y, width, fontScale = 1, imageCallback } = options;
    const s = fontScale;
    const src = block.src || "";
    const caption = block.caption || "";
    const imgWidth = Math.min(block.width || width, width);
    const alignment = block.alignment || "left";

    if (!src) return null;

    const group = new Konva.Group({ x, y, width });

    // Resolve image from crystallized data or literal src
    let imgSrc = src;
    if (crystalizedData && crystalizedData[src]) {
      imgSrc = crystalizedData[src];
    }

    // Image placeholder for now (in real usage, CustomImage would be used)
    const imgX = alignment === "center"
      ? (width - imgWidth) / 2
      : alignment === "right"
        ? width - imgWidth
        : 0;

    // Use a Rect as placeholder — real implementation would use CustomImage
    const img = new Konva.Rect({
      x: imgX, y: 0,
      width: imgWidth, height: Math.min(imgWidth * 0.65, 300),
      fill: "#f5f5f5",
      stroke: "#ddd",
      strokeWidth: 1
    });
    group.add(img);

    // Caption
    if (caption) {
      const captionNode = new CustomText({
        x: 0, y: img.height() + 4,
        width,
        text: caption,
        fontSize: Math.round(10 * s),
        fill: "#666",
        align: alignment
      });
      group.add(captionNode);
      group.height(img.height() + 4 + captionNode.height());
    } else {
      group.height(img.height());
    }

    // Semantic tagging
    group.setAttr("_semantic", {
      blockType: "image",
      src
    });

    return group;
  }

  /**
   * renderConditional — wraps existing VizDsl conditional renderer.
   */
  renderConditional(block, crystalizedData, options) {
    const rootData = crystalizedData[block.id] || {};
    const scopedData = { ...crystalizedData, ...rootData };

    for (const branch of (block.branches || [])) {
      if (evaluateCondition(branch.when, scopedData)) {
        return this.renderBlock(branch.then, scopedData, options);
      }
    }
    return null;
  }

  /**
   * renderVizBlock — delegate to existing VizDsl renderer, add semantic tags.
   */
  renderVizBlock(block, crystalizedData, options) {
    const { x, y, width, height, fontScale, theme, debug, imageCallback } = options;

    // Map to existing VizDsl renderers
    let group = null;
    switch (block.type) {
      case "chart":
        group = vizRenderChart(block, crystalizedData, {
          x, y, width, height,
          imageCallback, theme, fontScale, debug
        });
        group?.setAttr("_semantic", { blockType: "chart", chartKind: block.kind, requiresImage: true });
        break;
      case "tile_row":
        group = vizRenderTileRow(block, crystalizedData, {
          x, y, width, height,
          imageCallback, theme, fontScale, debug
        });
        group?.setAttr("_semantic", { blockType: "tile_row", requiresImage: true });
        break;
      case "bullet_list":
        group = vizRenderBulletList(block, crystalizedData, {
          x, y, width, height,
          imageCallback, theme, fontScale, debug
        });
        group?.setAttr("_semantic", { blockType: "bulletList", itemStyle: block.item_style, requiresImage: true });
        break;
      case "map":
        group = vizRenderMap(block, crystalizedData, {
          x, y, width, height,
          imageCallback, theme, fontScale, debug
        });
        group?.setAttr("_semantic", { blockType: "map", requiresImage: true });
        break;
      case "text_block":
        group = vizRenderTextBlock(block, crystalizedData, {
          x, y, width, height,
          imageCallback, theme, fontScale, debug
        });
        group?.setAttr("_semantic", { blockType: "textBlock", requiresImage: true });
        break;
      case "legend":
        group = vizRenderLegend(block, crystalizedData, {
          x, y, width, height,
          imageCallback, theme, fontScale, debug
        });
        group?.setAttr("_semantic", { blockType: "legend", requiresImage: true });
        break;
      default:
        return null;
    }

    return group;
  }
}

// ── Condition evaluator (reused from VizDslRenderer) ────────────────────

function evaluateCondition(condition, data) {
  if (!condition) return true;
  const { field, op, value } = condition;
  const dataValue = field.split(".").reduce((obj, key) => (obj != null ? obj[key] : undefined), data);

  switch (op) {
    case "eq": return dataValue === value;
    case "neq": return dataValue !== value;
    case "gt": return Number(dataValue) > Number(value);
    case "lt": return Number(dataValue) < Number(value);
    case "gte": return Number(dataValue) >= Number(value);
    case "lte": return Number(dataValue) <= Number(value);
    case "in": return Array.isArray(value) && value.includes(dataValue);
    default: return false;
  }
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

export function renderDocDslDocument(dsl, crystalizedData, options = {}) {
  const {
    width = DEFAULT_PAGE_WIDTH,
    height = DEFAULT_PAGE_HEIGHT,
    imageCallback,
    theme,
    debug = false
  } = options;

  const pageConfig = {
    ...(dsl.page || {}),
    width: dsl.page?.width || width,
    height: dsl.page?.height || height
  };

  // Font scale is relative to the document's actual page width, not the
  // preview frame width. At reference width (816px = US Letter), fonts are 1x.
  const docWidth = pageConfig.width;
  const fontScale = options.fontScale || (docWidth / DEFAULT_PAGE_WIDTH);

  const engine = new DocPaginationEngine(
    pageConfig,
    dsl.chrome || null,
    { imageCallback, theme, fontScale, debug, provenance: options.provenance }
  );

  const flow = dsl.flow || [];
  const pages = engine.buildDocument(flow, crystalizedData);

  return {
    pages,
    engine
  };
}

export default { renderDocDslDocument, DocPaginationEngine };
